/**
 * Colour Utility Functions
 *
 * Pure, dependency-free colour manipulation helpers for brand theming.
 * All conversions follow WCAG 2.1 and OKLCH colour-space standards.
 */

/** Parse a hex colour string to an RGB object. Supports 3- and 6-digit hex. */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalised = hex.trim().replace('#', '');
  const isShort = normalised.length === 3;
  const isValid = isShort || normalised.length === 6;
  if (!isValid || !/^[0-9a-fA-F]+$/.test(normalised)) {
    return null;
  }
  const full = isShort
    ? normalised
        .split('')
        .map((ch) => `${ch}${ch}`)
        .join('')
    : normalised;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

/**
 * WCAG 2.1 relative luminance.
 * @see https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
export function relativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * WCAG contrast ratio between two luminance values.
 * Returns a value ≥ 1 (e.g. 4.5 for AA normal text).
 */
export function contrastRatio(lum1: number, lum2: number): number {
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Return '#ffffff' or '#000000' — whichever provides better WCAG AA contrast
 * against the given background hex colour.
 */
export function getContrastForeground(hexBg: string): '#ffffff' | '#000000' {
  const rgb = hexToRgb(hexBg);
  if (!rgb) return '#000000';
  const bgLum = relativeLuminance(rgb.r, rgb.g, rgb.b);
  const whiteLum = 1; // relativeLuminance(255, 255, 255)
  const blackLum = 0; // relativeLuminance(0, 0, 0)
  const whiteContrast = contrastRatio(bgLum, whiteLum);
  const blackContrast = contrastRatio(bgLum, blackLum);
  return whiteContrast >= blackContrast ? '#ffffff' : '#000000';
}

/** WCAG 2.1 non-text contrast minimum for UI components (SC 1.4.11). */
export const NON_TEXT_CONTRAST_MIN = 3;

/** WCAG 2.1 AA contrast minimum for normal-size text (SC 1.4.3). */
export const TEXT_CONTRAST_MIN = 4.5;

/**
 * Lightest dark-mode surface a branded control sits on (`--muted` /
 * `--secondary` in `.dark`, ≈ oklch(0.269 0 0)). Clearing this surface also
 * clears the darker page and card backgrounds.
 */
export const DARK_MODE_SURFACE = '#262626';

/** Light-mode page background (`--background` in `:root`). */
export const LIGHT_MODE_SURFACE = '#ffffff';

/**
 * WCAG contrast ratio between two hex colours. Returns 1 when either colour
 * cannot be parsed.
 */
export function contrastBetween(hexA: string, hexB: string): number {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return 1;
  return contrastRatio(
    relativeLuminance(a.r, a.g, a.b),
    relativeLuminance(b.r, b.g, b.b),
  );
}

/** Format RGB channels (0–255) as a lowercase `#rrggbb` string. */
export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (v: number) =>
    Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Lighten a brand colour just enough to stand out against the dark-mode
 * surface (WCAG non-text contrast). Colours that already pass are returned
 * unchanged; otherwise the colour is blended towards white in small steps so
 * the hue stays recognisable. Unparseable input is returned as-is.
 *
 * @param hex  Brand colour in hex
 * @param surface  Background the colour sits on (default dark-mode surface)
 * @param minRatio  Required contrast ratio (default 3:1)
 */
export function ensureContrastOnDark(
  hex: string,
  surface: string = DARK_MODE_SURFACE,
  minRatio: number = NON_TEXT_CONTRAST_MIN,
): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  if (contrastBetween(hex, surface) >= minRatio) return hex;

  for (let step = 1; step <= 20; step++) {
    const t = step / 20;
    const candidate = rgbToHex(
      rgb.r + (255 - rgb.r) * t,
      rgb.g + (255 - rgb.g) * t,
      rgb.b + (255 - rgb.b) * t,
    );
    if (contrastBetween(candidate, surface) >= minRatio) return candidate;
  }
  return '#ffffff';
}

/* ------------------------------------------------------------------ */
/*  Hex → OKLCH conversion (pure maths, no external dependencies)     */
/* ------------------------------------------------------------------ */

/** Linear sRGB → CIE XYZ (D65). */
function linearRgbToXyz(lr: number, lg: number, lb: number): [number, number, number] {
  return [
    0.4123907993 * lr + 0.3575843394 * lg + 0.1804807884 * lb,
    0.2126390059 * lr + 0.7151686788 * lg + 0.0721923154 * lb,
    0.0193308187 * lr + 0.1191947798 * lg + 0.9505321522 * lb,
  ];
}

/** CIE XYZ → Oklab. */
function xyzToOklab(x: number, y: number, z: number): [number, number, number] {
  const l_ = Math.cbrt(0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z);
  const m_ = Math.cbrt(0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z);
  const s_ = Math.cbrt(0.0482003018 * x + 0.2643662691 * y + 0.6338517070 * z);

  const L = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
  const bVal = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;
  return [L, a, bVal];
}

/** Convert a hex colour to OKLCH components. */
export function hexToOklch(hex: string): { l: number; c: number; h: number } {
  const rgb = hexToRgb(hex);
  if (!rgb) return { l: 0, c: 0, h: 0 };

  // sRGB → linear sRGB
  const toLinear = (v: number) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const lr = toLinear(rgb.r);
  const lg = toLinear(rgb.g);
  const lb = toLinear(rgb.b);

  const [x, y, z] = linearRgbToXyz(lr, lg, lb);
  const [L, a, b] = xyzToOklab(x, y, z);

  const c = Math.sqrt(a * a + b * b);
  let h = (Math.atan2(b, a) * 180) / Math.PI;
  if (h < 0) h += 360;

  return {
    l: Math.round(L * 1000) / 1000,
    c: Math.round(c * 1000) / 1000,
    h: Math.round(h * 100) / 100,
  };
}

/** Format an OKLCH value as a CSS string. */
function formatOklch(l: number, c: number, h: number): string {
  return `oklch(${l.toFixed(3)} ${c.toFixed(4)} ${h.toFixed(2)})`;
}

/**
 * Generate a very light tint of a colour suitable for light-mode backgrounds.
 * High lightness, low saturation — keeps the hue recognisable without being
 * overpowering.
 *
 * @param hex  Source colour in hex
 * @param amount  0–1 blend strength (default 0.92 = very light)
 * @returns An `oklch(...)` CSS string
 */
export function tintForLightMode(hex: string, amount: number = 0.92): string {
  const { c, h } = hexToOklch(hex);
  // Push lightness very high, reduce chroma significantly
  const l = 0.97 * amount + (1 - amount) * 0.85;
  const tintC = c * (1 - amount) * 0.6;
  return formatOklch(l, tintC, h);
}

/**
 * Generate a dark tint of a colour suitable for dark-mode backgrounds.
 * Low lightness, reduced saturation — keeps the hue present without
 * clashing with light text.
 *
 * @param hex  Source colour in hex
 * @param amount  0–1 blend strength (default 0.85)
 * @returns An `oklch(...)` CSS string
 */
export function tintForDarkMode(hex: string, amount: number = 0.85): string {
  const { c, h } = hexToOklch(hex);
  // Push lightness low, keep a hint of chroma
  const l = 0.2 * amount + (1 - amount) * 0.35;
  const tintC = c * (1 - amount) * 0.5;
  return formatOklch(l, tintC, h);
}

